/**
 * localCharacters — multi-character storage for offline / local-mode play.
 *
 * Local mode now mirrors the account-mode multi-character model:
 *   - Up to 3 characters in a single JSON blob at `cve.characters.v1`
 *   - One is marked `activeId`; the character-select screen renders the
 *     full list, the picked id is loaded into gameStore.player.
 *   - Legacy single-character saves at `cve.game.v1` (Zustand persist) are
 *     migrated on first read into slot 0 so existing users don't lose
 *     their progress when they update.
 *
 * gameStore.player still gets persisted to `cve.game.v1` by Zustand (we
 * keep that for the active character's working copy). On every meaningful
 * change, callers should `syncActiveCharacter(player)` to push the latest
 * state back into the list so the select-screen shows fresh level/lastTick
 * values when the user comes back from a Settings → Switch Character.
 */
import type { CharacterSummary, Player } from '@/domain/types';

const KEY = 'cve.characters.v1';
const LEGACY_KEY = 'cve.game.v1';

interface LocalCharactersBlob {
  activeId: string | null;
  characters: Player[];
}

const EMPTY: LocalCharactersBlob = { activeId: null, characters: [] };

function readBlob(): LocalCharactersBlob {
  // Path 1: modern multi-char blob already exists.
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as LocalCharactersBlob;
      if (Array.isArray(parsed.characters)) return parsed;
    }
  } catch {
    // fall through to legacy migration
  }

  // Path 2: legacy single-character zustand-persist save. Promote to slot 0
  // and write back as the multi-char blob.
  try {
    const legacyRaw = localStorage.getItem(LEGACY_KEY);
    if (legacyRaw) {
      const parsed = JSON.parse(legacyRaw);
      const player: Player | undefined = parsed?.state?.player;
      if (player && typeof player.id === 'string' && player.id.startsWith('local-')) {
        const migrated: LocalCharactersBlob = {
          activeId: player.id,
          characters: [{
            ...player,
            userId: null,
            characterIndex: 0,
            characterClass: player.characterClass ?? 'hollow_knight',
          }],
        };
        writeBlob(migrated);
        return migrated;
      }
    }
  } catch {
    // fall through
  }

  return { ...EMPTY };
}

function writeBlob(blob: LocalCharactersBlob): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(blob));
  } catch (err) {
    console.warn('[localCharacters] write failed', err);
  }
}

function toSummary(p: Player): CharacterSummary {
  return {
    id: p.id,
    userId: null,
    characterIndex: p.characterIndex ?? 0,
    characterClass: p.characterClass ?? 'hollow_knight',
    name: p.name,
    level: p.level,
    lastTickAt: p.lastTickAt,
  };
}

// ── Public API ────────────────────────────────────────────────

export function listLocalCharacters(): CharacterSummary[] {
  return readBlob().characters.map(toSummary);
}

export function getActiveLocalCharacterId(): string | null {
  return readBlob().activeId;
}

export function loadLocalCharacter(id: string): Player | null {
  return readBlob().characters.find((p) => p.id === id) ?? null;
}

/** Inserts an already-seeded player into the local list at its declared
 *  `characterIndex`. Caller is responsible for setting characterIndex /
 *  characterClass / userId on the player before calling. Marks it active. */
export function addLocalCharacter(player: Player): void {
  const blob = readBlob();
  const idx = player.characterIndex ?? 0;
  // Reject if slot is already taken — caller must call deleteLocalCharacter
  // first if they want to replace.
  if (blob.characters.some((c) => c.characterIndex === idx)) {
    throw new Error(`Local slot ${idx} already filled`);
  }
  blob.characters.push(player);
  blob.activeId = player.id;
  writeBlob(blob);
}

export function deleteLocalCharacter(id: string): void {
  const blob = readBlob();
  blob.characters = blob.characters.filter((c) => c.id !== id);
  if (blob.activeId === id) {
    blob.activeId = blob.characters[0]?.id ?? null;
  }
  writeBlob(blob);
}

export function setActiveLocalCharacter(id: string | null): void {
  const blob = readBlob();
  blob.activeId = id;
  writeBlob(blob);
}

/** Replaces the in-list snapshot of the given character with the supplied
 *  player object. Called on character-switch boundary + periodically while
 *  in-game so the select-screen always shows fresh level / lastTickAt. */
export function syncActiveCharacter(player: Player): void {
  const blob = readBlob();
  const idx = blob.characters.findIndex((c) => c.id === player.id);
  if (idx >= 0) {
    blob.characters[idx] = player;
    writeBlob(blob);
  } else if (player.id.startsWith('local-')) {
    // Defensive: if somehow the active character isn't in the list, append it.
    blob.characters.push(player);
    blob.activeId = player.id;
    writeBlob(blob);
  }
}

/** Find the lowest unused slot (0..2). Returns null if all slots are full. */
export function nextFreeLocalSlot(maxSlots: number): number | null {
  const blob = readBlob();
  const used = new Set(blob.characters.map((c) => c.characterIndex ?? 0));
  for (let i = 0; i < maxSlots; i++) {
    if (!used.has(i)) return i;
  }
  return null;
}

/** Wipe everything — used by "Konto erstellen + übertragen" once the local
 *  characters have been successfully copied into the Supabase account. */
export function clearAllLocalCharacters(): void {
  try {
    localStorage.removeItem(KEY);
  } catch (err) {
    console.warn('[localCharacters] clear failed', err);
  }
}
